/**
 * Layout 레이아웃 관리 Repository
 * API 호출 추상화 계층
 */

import { API_BASE_URL } from '../../../config/api';
import type {
  Room,
  Seat,
  Zone,
  Device,
  ArchitectureElement,
  ZoneCreateRequest,
  SeatPositionUpdateRequest,
  DevicePositionUpdateRequest,
} from '../models/Layout';

export class LayoutRepository {
  private async apiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('⚠️ [apiCall] accessToken이 없습니다. 로그인이 필요합니다.');
    }

    const fullUrl = `${API_BASE_URL}${url}`;
    console.log(`🌐 [apiCall] ${options.method || 'GET'} ${fullUrl}`);
    if (options.body) {
      console.log(`📦 [apiCall] Request body:`, options.body);
    }

    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || 'no-token'}`,
        ...options.headers,
      },
    });

    console.log(`📬 [apiCall] Response status: ${response.status}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      console.error(`❌ [apiCall] HTTP ${response.status}: ${errorData.detail}`);
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ [apiCall] Response data:`, data);
    return data;
  }

  // ==================== Room API ====================
  async getRooms(): Promise<Room[]> {
    try {
      return await this.apiCall<Room[]>('/v1/seat-config/rooms');
    } catch (error) {
      console.error('룸 목록 조회 실패:', error);
      throw error;
    }
  }

  async createRoom(data: { name: string; description?: string; grid_width: number; grid_height: number }): Promise<Room> {
    return await this.apiCall<Room>('/v1/seat-config/rooms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRoomGridSize(roomId: string, width: number, height: number): Promise<Room> {
    return await this.apiCall<Room>(`/v1/seat-config/rooms/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify({ grid_width: width, grid_height: height }),
    });
  }

  async updateRoom(roomId: string, data: { name?: string; description?: string }): Promise<Room> {
    return await this.apiCall<Room>(`/v1/seat-config/rooms/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRoom(roomId: string): Promise<void> {
    await this.apiCall<void>(`/v1/seat-config/rooms/${roomId}`, {
      method: 'DELETE',
    });
  }

  async reorderRooms(roomIds: string[]): Promise<void> {
    await this.apiCall<void>('/v1/seat-config/rooms/reorder', {
      method: 'PUT',
      body: JSON.stringify({ room_ids: roomIds }),
    });
  }

  // ==================== Zone API ====================
  async getZones(roomId?: string): Promise<Zone[]> {
    try {
      const url = roomId ? `/v1/seat-config/zones?room_id=${roomId}` : '/v1/seat-config/zones';
      return await this.apiCall<Zone[]>(url);
    } catch (error) {
      console.error('구역 목록 조회 실패:', error);
      throw error;
    }
  }

  async createZone(data: ZoneCreateRequest): Promise<Zone> {
    try {
      const result = await this.apiCall<Zone>('/v1/seat-config/zones', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return result;
    } catch (error) {
      console.error('❌ [createZone] 에러 발생:', error);
      throw error;
    }
  }

  async updateZone(zoneId: string, data: ZoneCreateRequest): Promise<Zone> {
    return await this.apiCall<Zone>(`/v1/seat-config/zones/${zoneId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZone(zoneId: string): Promise<void> {
    await this.apiCall<void>(`/v1/seat-config/zones/${zoneId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Seat API ====================
  async getSeats(roomId?: string): Promise<Seat[]> {
    try {
      const url = roomId ? `/v1/seat-config/seats?room_id=${roomId}` : '/v1/seat-config/seats';
      const response = await this.apiCall<{ seats: Seat[]; total: number } | Seat[]>(url);
      // API 응답이 {seats: [...]} 형식인지 배열인지 확인
      let seats: Seat[];
      if (Array.isArray(response)) {
        seats = response;
      } else {
        seats = response.seats || [];
      }

      // 디버그: 좌석 좌표 확인
      if (seats.length > 0) {
        console.log('🪑 [getSeats] 좌표 샘플:', seats.slice(0, 3).map(s => ({
          seat_number: s.seat_number,
          position_x: s.position_x,
          position_y: s.position_y,
          room_id: s.room_id
        })));
      }

      return seats;
    } catch (error) {
      console.error('좌석 목록 조회 실패:', error);
      return []; // 에러 시 빈 배열 반환
    }
  }

  async createSeat(data: any): Promise<Seat> {
    return await this.apiCall<Seat>('/v1/seat-config/seats', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSeatPosition(seatNumber: number, data: SeatPositionUpdateRequest): Promise<void> {
    try {
      console.log(`🔄 [updateSeatPosition] API 호출 준비: 좌석 ${seatNumber}`, data);
      const url = `/v1/seat-config/seats/${seatNumber}`;
      console.log(`📡 [updateSeatPosition] PUT ${url}`);
      await this.apiCall<void>(url, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      console.log(`✅ [updateSeatPosition] API 호출 성공: 좌석 ${seatNumber}`);
    } catch (error) {
      console.error(`❌ [updateSeatPosition] 좌석 ${seatNumber} 업데이트 실패:`, error);
      throw error;
    }
  }

  async deleteSeat(seatNumber: number): Promise<void> {
    await this.apiCall<void>(`/v1/seat-config/seats/${seatNumber}`, {
      method: 'DELETE',
    });
  }

  // ==================== Device API ====================
  async getDevices(): Promise<Device[]> {
    try {
      return await this.apiCall<Device[]>('/v1/devices');
    } catch (error) {
      return [];
    }
  }

  async createDevice(data: Partial<Device>): Promise<Device> {
    return await this.apiCall<Device>('/v1/devices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDevice(deviceId: string, data: Partial<Device>): Promise<Device> {
    return await this.apiCall<Device>(`/v1/devices/${deviceId}`, {
      method: 'PUT',
      body: JSON.stringify({
        device_name: data.device_name,
        device_type: data.device_type,
        network_info: data.network_info,
        attributes: data.attributes,
      }),
    });
  }

  async updateDevicePosition(deviceId: string, data: DevicePositionUpdateRequest): Promise<void> {
    await this.apiCall<void>(`/v1/devices/${deviceId}/position`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDevice(deviceId: string): Promise<void> {
    await this.apiCall<void>(`/v1/devices/${deviceId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Architecture (localStorage) ====================
  loadArchitectureElements(roomId: string): ArchitectureElement[] {
    try {
      const saved = localStorage.getItem(`architecture_${roomId}`);
      if (saved) {
        return JSON.parse(saved);
      }
      return [];
    } catch (error) {
      console.error('건축 요소 로드 실패:', error);
      return [];
    }
  }

  saveArchitectureElements(roomId: string, elements: ArchitectureElement[]): void {
    try {
      const roomElements = elements.filter((el) => el.room_id === roomId);
      localStorage.setItem(`architecture_${roomId}`, JSON.stringify(roomElements));
    } catch (error) {
      console.error('건축 요소 저장 실패:', error);
      throw error;
    }
  }
}

export const layoutRepository = new LayoutRepository();
